import dayjs, { type Dayjs } from 'dayjs'
import type { TabletDeviceEvent, TabletLifecycleEvent } from '@/types/types'

export const computeMedian = (values: number[]): number | null => {
  if (!values.length) return null

  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)

  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

export type DayGroup<T> = {
  key: string
  items: T[]
}

export const groupItemsByDay = <T>(items: T[], getSortAt: (item: T) => string): DayGroup<T>[] => {
  const groups = new Map<string, T[]>()

  for (const item of items) {
    const key = dayjs(getSortAt(item)).format('YYYY-MM-DD')
    const group = groups.get(key)
    if (group) group.push(item)
    else groups.set(key, [item])
  }

  return Array.from(groups.entries()).map(([key, groupItems]) => ({ key, items: groupItems }))
}

export const dayRelativeLabel = (dayKey: string, reference: Dayjs = dayjs()): 'today' | 'yesterday' | null => {
  const day = dayjs(dayKey)
  if (day.isSame(reference, 'day')) return 'today'
  if (day.isSame(reference.subtract(1, 'day'), 'day')) return 'yesterday'
  return null
}

export type TabletEventFieldChange = { field: string; from: unknown; to: unknown }

export type TabletEventMetadataSummary =
  | { kind: 'changes'; changes: TabletEventFieldChange[] }
  // Rows written before backend#9435 only kept the field names, no from/to.
  | { kind: 'fieldsOnly'; fields: string[] }
  | null

const isFromToPair = (value: unknown): value is { from: unknown; to: unknown } =>
  typeof value === 'object' && value !== null && 'from' in value && 'to' in value

export const summarizeTabletEventMetadata = (metadata: TabletDeviceEvent['metadata']): TabletEventMetadataSummary => {
  if (!metadata) return null

  if (metadata.changes && typeof metadata.changes === 'object') {
    const changes = Object.entries(metadata.changes as Record<string, unknown>)
      .filter((entry): entry is [string, { from: unknown; to: unknown }] => isFromToPair(entry[1]))
      .map(([field, { from, to }]) => ({ field, from, to }))

    if (changes.length) return { kind: 'changes', changes }
  }

  if (Array.isArray(metadata.changedFields) && metadata.changedFields.length) {
    return { kind: 'fieldsOnly', fields: metadata.changedFields as string[] }
  }

  return null
}

export const formatTabletEventChangeValue = (
  value: unknown,
  booleanLabels: { on: string; off: string },
  emptyLabel: string
): string => {
  if (typeof value === 'boolean') return value ? booleanLabels.on : booleanLabels.off
  if (value === null || value === undefined || value === '') return emptyLabel
  return String(value)
}

// French-style duration regardless of the dashboard's active language, matching the
// comma-decimal convention already forced on dates elsewhere in this modal.
export const formatOfflineDuration = (ms: number): string => {
  const totalSeconds = Math.round(ms / 1000)
  if (totalSeconds < 60) return `${totalSeconds} s`
  const totalMinutes = Math.round(totalSeconds / 60)
  if (totalMinutes < 60) return `${totalMinutes} min`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return minutes ? `${hours} h ${String(minutes).padStart(2, '0')}` : `${hours} h`
}

export type TabletOfflineMetadata = { offlineDurationMs: number; reconnectAttempts: number }

export const readTabletOfflineMetadata = (metadata: TabletDeviceEvent['metadata']): TabletOfflineMetadata | null => {
  if (!metadata || typeof metadata.offlineDurationMs !== 'number') return null
  return {
    offlineDurationMs: metadata.offlineDurationMs,
    reconnectAttempts: typeof metadata.reconnectAttempts === 'number' ? metadata.reconnectAttempts : 0
  }
}

export const formatLatency = (ms: number): string => {
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1).replace('.', ',')} s`
}

// Groups adjacent items sharing the same key — used to collapse consecutive identical
// tablet events (e.g. two auto-logins at the exact same timestamp) into one row with a count.
export const mergeConsecutiveByKey = <T>(items: T[], keyFn: (item: T) => string): T[][] => {
  const groups: T[][] = []

  for (const item of items) {
    const lastGroup = groups[groups.length - 1]
    if (lastGroup && keyFn(lastGroup[0]) === keyFn(item)) lastGroup.push(item)
    else groups.push([item])
  }

  return groups
}

export type DisconnectSeverity = 'benign' | 'watch' | 'concerning'

// AWS IoT Core's disconnectReason enum, triaged for a support agent: benign codes are normal
// tablet/app lifecycle noise, "watch" codes are fine once but worth a second look if repeated,
// "concerning" codes need investigation. Codes absent here (including a genuinely unknown/null
// reason) fall back to "watch" — treat as a plain network blip without over-interpreting it.
const DISCONNECT_REASON_SEVERITY: Record<string, DisconnectSeverity> = {
  CLIENT_INITIATED_DISCONNECT: 'benign',
  SERVER_INITIATED_DISCONNECT: 'benign',
  API_INITIATED_DISCONNECT: 'benign',
  DUPLICATE_CLIENTID: 'benign',
  WEBSOCKET_TTL_EXPIRATION: 'benign',
  CUSTOMAUTH_TTL_EXPIRATION: 'benign',
  CONNECTION_LOST: 'watch',
  MQTT_KEEP_ALIVE_TIMEOUT: 'watch',
  CLIENT_ERROR: 'watch',
  THROTTLED: 'concerning',
  AUTH_ERROR: 'concerning',
  FORBIDDEN_ACCESS: 'concerning',
  SERVER_ERROR: 'concerning'
}

export const getDisconnectSeverity = (reason: string | null): DisconnectSeverity =>
  (reason && DISCONNECT_REASON_SEVERITY[reason]) || 'watch'

// The i18n key segment for a reason's label/explanation — any code we don't recognize
// (including null) reads as "UNKNOWN" rather than leaking a raw, untranslated AWS code.
export const getDisconnectReasonKey = (reason: string | null): string =>
  reason && reason in DISCONNECT_REASON_SEVERITY ? reason : 'UNKNOWN'

export const ORDER_SENT_NEARBY_LIFECYCLE_MS = 120_000

export const lifecycleEventsNearTimestamp = (
  timestamp: string,
  events: TabletLifecycleEvent[],
  windowMs = ORDER_SENT_NEARBY_LIFECYCLE_MS
): TabletLifecycleEvent[] => {
  const anchor = dayjs(timestamp)

  return events.filter((event) => Math.abs(anchor.diff(dayjs(event.timestamp))) <= windowMs)
}

export type UnackedConnectionHint = 'duplicate_client_id' | 'flapping' | 'disconnected'

export const connectionHintForUnackedOrder = (
  sentToTabletAt: string,
  events: TabletLifecycleEvent[]
): UnackedConnectionHint | null => {
  const nearby = lifecycleEventsNearTimestamp(sentToTabletAt, events)
  if (!nearby.length) {
    return null
  }

  const hasDuplicate = nearby.some(
    (event) => event.eventType === 'disconnected' && event.disconnectReason === 'DUPLICATE_CLIENTID'
  )
  if (hasDuplicate) {
    return 'duplicate_client_id'
  }

  const hasFlapping = nearby.some((event) => event.eventType === 'flapping')
  if (hasFlapping) {
    return 'flapping'
  }

  const hasSignificantDisconnect = nearby.some(
    (event) => event.eventType === 'disconnected' && getDisconnectSeverity(event.disconnectReason) !== 'benign'
  )
  if (hasSignificantDisconnect) {
    return 'disconnected'
  }

  return null
}

export const formatPosDeviceLabel = (name: string | null | undefined, deviceId: string): string => {
  const trimmed = name?.trim()
  if (trimmed) {
    return `${trimmed} (#${deviceId})`
  }

  return `#${deviceId}`
}

export type DeviceLinkSummaryKey = 'added' | 'changed' | 'removed' | 'removedTransferred'

export const resolveDeviceLinkSummary = (
  event: TabletDeviceEvent
): { key: DeviceLinkSummaryKey; params: Record<string, string | number> } | null => {
  if (event.eventType === 'added' && event.deviceId) {
    return {
      key: 'added',
      params: { pos: formatPosDeviceLabel(event.deviceName, event.deviceId) }
    }
  }

  if (event.eventType === 'removed' && event.previousDeviceId) {
    const pos = formatPosDeviceLabel(event.previousDeviceName, event.previousDeviceId)
    if (event.transferredToTabletId) {
      return { key: 'removedTransferred', params: { pos, tabletId: event.transferredToTabletId } }
    }

    return { key: 'removed', params: { pos } }
  }

  if (event.eventType === 'changed' && event.previousDeviceId && event.deviceId) {
    return {
      key: 'changed',
      params: {
        previousPos: formatPosDeviceLabel(event.previousDeviceName, event.previousDeviceId),
        nextPos: formatPosDeviceLabel(event.deviceName, event.deviceId)
      }
    }
  }

  return null
}
